import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAdminSuccessStories, QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import {
  BookOpen,
  Edit,
  Save,
  Plus,
  Trash2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import type { SuccessStory } from "@shared/schema";

export function SuccessStoriesDashboard() {
  const { toast } = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingStory, setEditingStory] = useState<SuccessStory | null>(null);

  const [formName, setFormName] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formCondition, setFormCondition] = useState("");
  const [formConditionColor, setFormConditionColor] = useState("bg-blue-500/10 text-blue-600 dark:text-blue-400");
  const [formQuote, setFormQuote] = useState("");
  const [formCareAccessed, setFormCareAccessed] = useState("");
  const [formFundsRaised, setFormFundsRaised] = useState("");
  const [formContributors, setFormContributors] = useState("");
  const [formWalletType, setFormWalletType] = useState("");
  const [formTimeframe, setFormTimeframe] = useState("");
  const [formIconType, setFormIconType] = useState("stethoscope");
  const [formDisplayOrder, setFormDisplayOrder] = useState("0");
  const [formIsActive, setFormIsActive] = useState(true);
  const [formIsFeatured, setFormIsFeatured] = useState(false);

  const { data: stories = [], isLoading } = useAdminSuccessStories();

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await apiRequest("POST", "/api/admin/success-stories", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminSuccessStories });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.successStories });
      resetForm();
      setShowCreate(false);
      toast({ title: "Story Created", description: "Success story has been added." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await apiRequest("PATCH", `/api/admin/success-stories/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminSuccessStories });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.successStories });
      resetForm();
      setEditingStory(null);
      toast({ title: "Story Updated", description: "Success story has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      await apiRequest("PATCH", `/api/admin/success-stories/${id}/toggle`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminSuccessStories });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.successStories });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/success-stories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.adminSuccessStories });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.successStories });
      toast({ title: "Story Deleted", description: "Success story has been removed." });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function resetForm() {
    setFormName("");
    setFormLocation("");
    setFormCondition("");
    setFormConditionColor("bg-blue-500/10 text-blue-600 dark:text-blue-400");
    setFormQuote("");
    setFormCareAccessed("");
    setFormFundsRaised("");
    setFormContributors("");
    setFormWalletType("");
    setFormTimeframe("");
    setFormIconType("stethoscope");
    setFormDisplayOrder("0");
    setFormIsActive(true);
    setFormIsFeatured(false);
  }

  function openEdit(story: SuccessStory) {
    setEditingStory(story);
    setFormName(story.name);
    setFormLocation(story.location);
    setFormCondition(story.condition);
    setFormConditionColor(story.conditionColor);
    setFormQuote(story.quote);
    setFormCareAccessed(story.careAccessed);
    setFormFundsRaised(story.fundsRaised);
    setFormContributors(String(story.contributors));
    setFormWalletType(story.walletType);
    setFormTimeframe(story.timeframe);
    setFormIconType(story.iconType);
    setFormDisplayOrder(String(story.displayOrder));
    setFormIsActive(story.isActive);
    setFormIsFeatured(story.isFeatured);
  }

  function handleSubmit() {
    const data = {
      name: formName,
      location: formLocation,
      condition: formCondition,
      conditionColor: formConditionColor,
      quote: formQuote,
      careAccessed: formCareAccessed,
      fundsRaised: formFundsRaised,
      contributors: parseInt(formContributors) || 0,
      walletType: formWalletType,
      timeframe: formTimeframe,
      iconType: formIconType,
      displayOrder: parseInt(formDisplayOrder) || 0,
      isActive: formIsActive,
      isFeatured: formIsFeatured,
    };
    if (editingStory) {
      updateMutation.mutate({ id: editingStory.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  const conditionColorOptions = [
    { value: "bg-blue-500/10 text-blue-600 dark:text-blue-400", label: "Blue" },
    { value: "bg-green-500/10 text-green-600 dark:text-green-400", label: "Green" },
    { value: "bg-purple-500/10 text-purple-600 dark:text-purple-400", label: "Purple" },
    { value: "bg-amber-500/10 text-amber-600 dark:text-amber-400", label: "Amber" },
    { value: "bg-red-500/10 text-red-600 dark:text-red-400", label: "Red" },
    { value: "bg-pink-500/10 text-pink-600 dark:text-pink-400", label: "Pink" },
    { value: "bg-teal-500/10 text-teal-600 dark:text-teal-400", label: "Teal" },
  ];

  const iconOptions = [
    { value: "stethoscope", label: "Stethoscope" },
    { value: "heart", label: "Heart" },
    { value: "users", label: "Family/Users" },
    { value: "activity", label: "Activity" },
    { value: "shield", label: "Shield" },
    { value: "zap", label: "Lightning" },
  ];

  const walletTypeOptions = [
    "Community Fund",
    "Family Wallet",
    "Individual + Employer Match",
    "Condition Community Fund",
    "Organization Wallet",
    "Individual Wallet",
  ];

  const activeCount = stories.filter(s => s.isActive).length;
  const featuredCount = stories.filter(s => s.isFeatured).length;

  const storyForm = (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g. Maria T." data-testid="input-story-name" />
        </div>
        <div className="space-y-2">
          <Label>Location</Label>
          <Input value={formLocation} onChange={e => setFormLocation(e.target.value)} placeholder="e.g. Minneapolis, MN" data-testid="input-story-location" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Medical Condition</Label>
          <Input value={formCondition} onChange={e => setFormCondition(e.target.value)} placeholder="e.g. Type 2 Diabetes" data-testid="input-story-condition" />
        </div>
        <div className="space-y-2">
          <Label>Condition Badge Color</Label>
          <Select value={formConditionColor} onValueChange={setFormConditionColor}>
            <SelectTrigger data-testid="select-story-color">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {conditionColorOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Quote</Label>
        <Textarea value={formQuote} onChange={e => setFormQuote(e.target.value)} placeholder="Patient testimonial quote..." rows={3} data-testid="input-story-quote" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Care Accessed</Label>
          <Input value={formCareAccessed} onChange={e => setFormCareAccessed(e.target.value)} placeholder="e.g. Insulin & Endocrinology" data-testid="input-story-care" />
        </div>
        <div className="space-y-2">
          <Label>Funds Raised</Label>
          <Input value={formFundsRaised} onChange={e => setFormFundsRaised(e.target.value)} placeholder="e.g. $2,400" data-testid="input-story-funds" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Contributors</Label>
          <Input type="number" value={formContributors} onChange={e => setFormContributors(e.target.value)} placeholder="47" data-testid="input-story-contributors" />
        </div>
        <div className="space-y-2">
          <Label>Wallet Type</Label>
          <Select value={formWalletType} onValueChange={setFormWalletType}>
            <SelectTrigger data-testid="select-story-wallet-type">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {walletTypeOptions.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Timeframe</Label>
          <Input value={formTimeframe} onChange={e => setFormTimeframe(e.target.value)} placeholder="e.g. 3 months" data-testid="input-story-timeframe" />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Icon</Label>
          <Select value={formIconType} onValueChange={setFormIconType}>
            <SelectTrigger data-testid="select-story-icon">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {iconOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Display Order</Label>
          <Input type="number" value={formDisplayOrder} onChange={e => setFormDisplayOrder(e.target.value)} data-testid="input-story-order" />
        </div>
        <div className="flex items-end gap-4 pb-1">
          <div className="flex items-center gap-2">
            <Switch checked={formIsActive} onCheckedChange={setFormIsActive} data-testid="switch-story-active-form" />
            <Label>Active</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={formIsFeatured} onCheckedChange={setFormIsFeatured} data-testid="switch-story-featured-form" />
            <Label>Featured</Label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Separator className="my-4" />
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Success Stories
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage community impact stories displayed on the landing page. {activeCount} active, {featuredCount} featured.
          </p>
        </div>
        <Dialog open={showCreate || !!editingStory} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditingStory(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setShowCreate(true); }} data-testid="button-add-story">
              <Plus className="w-4 h-4 mr-2" />
              Add Story
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStory ? "Edit Success Story" : "Add Success Story"}</DialogTitle>
              <DialogDescription>
                {editingStory ? "Update the community impact story details." : "Add a new community impact story to the landing page."}
              </DialogDescription>
            </DialogHeader>
            {storyForm}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCreate(false); setEditingStory(null); resetForm(); }} data-testid="button-cancel-story">
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending || !formName || !formQuote}
                data-testid="button-save-story"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingStory ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ScrollArea className="w-full">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead>Funds</TableHead>
                    <TableHead>Contributors</TableHead>
                    <TableHead>Wallet Type</TableHead>
                    <TableHead className="text-center">Visible</TableHead>
                    <TableHead className="text-center">Featured</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stories.map((story) => (
                    <TableRow key={story.id} className={!story.isActive ? "opacity-50" : ""} data-testid={`row-story-${story.id}`}>
                      <TableCell className="text-muted-foreground" data-testid={`text-story-order-${story.id}`}>
                        {story.displayOrder}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="font-medium" data-testid={`text-story-name-${story.id}`}>{story.name}</span>
                          <p className="text-xs text-muted-foreground">{story.location}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={story.conditionColor} data-testid={`badge-story-condition-${story.id}`}>
                          {story.condition}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`text-story-funds-${story.id}`}>{story.fundsRaised}</TableCell>
                      <TableCell data-testid={`text-story-contributors-${story.id}`}>{story.contributors}</TableCell>
                      <TableCell data-testid={`text-story-wallet-${story.id}`}>{story.walletType}</TableCell>
                      <TableCell className="text-center">
                        <Switch
                          checked={story.isActive}
                          onCheckedChange={(checked) => toggleMutation.mutate({ id: story.id, isActive: checked })}
                          data-testid={`switch-story-active-${story.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant={story.isFeatured ? "default" : "outline"} data-testid={`badge-story-featured-${story.id}`}>
                          {story.isFeatured ? "Yes" : "No"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => openEdit(story)}
                            data-testid={`button-edit-story-${story.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              if (confirm("Are you sure you want to delete this story?")) {
                                deleteMutation.mutate(story.id);
                              }
                            }}
                            data-testid={`button-delete-story-${story.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {stories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8" data-testid="text-no-stories">
                        No success stories yet. Add your first community impact story.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </>
  );
}
